<template>
  <div class="item-browser">
    <!-- Fixed header section -->
    <div class="item-browser-header">
      <!-- Search & Filters -->
      <div class="search-section mb-3">
        <div class="input-group">
          <input
            type="text"
            class="form-control"
            :placeholder="$t('search_items')"
            v-model="searchQuery"
            @input="onSearchInput"
          />
          <div class="input-group-append">
            <button class="btn btn-outline-secondary" type="button">
              <i class="fa fa-search"></i>
            </button>
            <button class="btn btn-outline-secondary" type="button" @click="clearFilters">
              <i class="fa fa-remove"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="filters-section mb-3" v-if="showFilters">
        <div class="row">
          <div class="col-md-6">
            <select class="form-control form-control-sm" v-model="selectedCategory">
              <option value="">{{ $t('all_categories') }}</option>
              <option value="AC">{{ $t('AC') }} {{ $t('components') }}</option>
              <option value="DC">{{ $t('DC') }} {{ $t('components') }}</option>
              <option value="STANDARD">{{ $t('STANDARD') }}</option>
            </select>
          </div>
          <div class="col-md-6">
            <select
              class="form-control form-control-sm"
              v-model="selectedSupplier"
              v-if="showSupplierFilter"
            >
              <option value="">{{ $t('all_suppliers') }}</option>
              <option value="supplier1">Supplier 1</option>
              <option value="supplier2">Supplier 2</option>
            </select>
          </div>
        </div>
      </div>

      <!-- View Mode Toggle -->
      <div class="view-controls mb-3 d-flex justify-content-between align-items-center">
        <div class="view-mode-toggle">
          <div class="btn-group btn-group-sm" role="group">
            <button
              type="button"
              class="btn"
              :class="viewMode === 'cards' ? 'btn-secondary' : 'btn-outline-secondary'"
              @click="viewMode = 'cards'"
            >
              <i class="fa fa-th"></i>
            </button>
            <button
              type="button"
              class="btn"
              :class="viewMode === 'table' ? 'btn-secondary' : 'btn-outline-secondary'"
              @click="viewMode = 'table'"
            >
              <i class="fa fa-list"></i>
            </button>
          </div>
        </div>
        <div class="results-info">
          <small class="text-muted">{{ totalItems }} {{ $t('items_found') }}</small>
        </div>
      </div>
    </div>

    <!-- Scrollable content area -->
    <div class="item-browser-content">
      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-4">
        <i class="fa fa-spinner fa-spin"></i> {{ $t('loading') }}
      </div>

      <!-- Items Display -->
      <div v-else-if="viewMode === 'cards'" class="items-grid">
        <div
          v-for="item in items"
          :key="item._id"
          class="item-card"
          :class="{ selected: isSelected(item) }"
        >
          <!-- Item info -->
          <div class="item-info">
            <h6 class="item-title">{{ item.description }}</h6>
            <p class="item-details">
              #{{ item.number.toString().padStart(4, '0') }}
              <span v-if="item.industryNr"
                >•
                {{ item.industryNr }}
              </span>
              <span v-if="item.wholesaleNr"
                >•
                {{ item.wholesaleNr }}
              </span>
            </p>
            <p class="item-details" v-if="item.priceCalculation">
              <span
                v-if="showPricing && item.priceCalculation.totalCustomerPrice"
                class="item-price"
                >€{{ item.priceCalculation.totalCustomerPrice }}
              </span>
              {{ $t(item.unit) }}
            </p>
            <p class="item-details" v-if="item.supplier">
              <small class="text-muted">{{ item.supplier }}</small>
            </p>
          </div>

          <!-- Action area -->
          <div class="item-actions">
            <div class="quantity-controls">
              <input v-model="item.quantity" type="number" min="1" class="quantity-input" />
              <button @click="addItem(item)" class="btn btn-sm btn-success">
                <i class="fa fa-plus"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Table View -->
      <div v-else class="items-table">
        <table class="table table-sm table-striped">
          <thead>
            <tr>
              <th>{{ $t('number') }}</th>
              <th>{{ $t('wholesaleNr') }}</th>
              <th>{{ $t('industryNr') }}</th>
              <th>{{ $t('description') }}</th>
              <th v-if="showSupplierFilter">{{ $t('supplier') }}</th>
              <th v-if="showPricing">{{ $t('price') }}</th>
              <th>{{ $t('quantity') }}</th>
              <th>{{ $t('actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item._id">
              <td>{{ item.number.toString().padStart(4, '0') }}</td>
              <td>{{ item.wholesaleNr }}</td>
              <td>{{ item.industryNr }}</td>
              <td>{{ item.description }}</td>
              <td v-if="showSupplierFilter">{{ item.supplier }}</td>
              <td v-if="showPricing">
                <span v-if="item.priceCalculation && item.priceCalculation.totalCustomerPrice">
                  €{{ item.priceCalculation.totalCustomerPrice }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td>
                <input
                  v-model="item.quantity"
                  type="number"
                  min="1"
                  class="form-control form-control-sm quantity-input"
                />
              </td>
              <td>
                <button @click="addItem(item)" class="btn btn-sm btn-success">
                  <i class="fa fa-plus"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-if="!isLoading && items.length === 0" class="empty-state text-center py-4">
        <i class="fa fa-search fa-2x text-muted mb-2"></i>
        <p class="text-muted">{{ $t('no_results') }}</p>
      </div>

      <!-- Load More Button -->
      <div class="load-more-section text-center py-3" v-if="hasMoreItems && !isLoading">
        <button class="btn btn-outline-secondary" @click="loadMoreItems" :disabled="loadingMore">
          <i class="fa fa-spinner fa-spin" v-if="loadingMore"></i>
          {{ loadingMore ? $t('loading') : $t('load_more') }}
        </button>
        <div class="mt-2">
          <small class="text-muted">
            {{ items.length }} / {{ totalItems }} {{ $t('item_inventory') }}
          </small>
        </div>
      </div>
    </div>

    <!-- Toast notifications -->
    <Toast />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Toast from 'primevue/toast';

export default {
  name: 'ItemBrowser',
  components: {
    Toast,
  },
  props: {
    // Context configuration
    context: {
      type: String,
      required: true,
      validator: (value) => ['order', 'project', 'inventory'].includes(value),
    },
    selectionMode: {
      type: String,
      default: 'quantity',
      validator: (value) => ['single', 'multiple', 'quantity'].includes(value),
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },

    showFilters: {
      type: Boolean,
      default: false,
    },
    showSupplierFilter: {
      type: Boolean,
      default: true,
    },
    showPricing: {
      type: Boolean,
      default: true,
    },
    defaultViewMode: {
      type: String,
      default: 'cards',
      validator: (value) => ['cards', 'table'].includes(value),
    },
    // Filter to show only inverters (solarPlant === 'INVERTER')
    filterInvertersOnly: {
      type: Boolean,
      default: false,
    },
    // Filter to show only solar panels (solarPlant === 'SOLAR_PANELS')
    filterSolarPanelsOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: '',
      selectedCategory: '',
      selectedSupplier: '',
      viewMode: this.defaultViewMode,
      searchTimeout: null,
      loadingMore: false,
      initialPageSize: 12,
      loadMorePageSize: 8,
      currentPageSize: 0, // Will be set by loadTableData()
      // Table state configuration for the item browser
      itemBrowserTableStateConfig: {
        name: `itemBrowser_${this.context}`,
        filters: {
          search: { value: null },
        },
        customFilters: {},
        sorting: { sortField: 'number', sortOrder: 1 },
        pagination: { page: 0, pageSize: 15 }, // Not used with progressive loading
      },
    };
  },
  computed: {
    ...mapGetters(['isLoading', 'getItems', 'getItemCount', 'getTableState']),
    tableStateStore() {
      return this.getTableState(this.itemBrowserTableStateConfig.name);
    },
    totalItems() {
      return this.getItemCount;
    },
    items() {
      // Use items directly from store with quantity property
      return (this.getItems || []).map((item) => ({
        ...item,
        quantity: item.quantity || 1,
      }));
    },
    hasMoreItems() {
      return this.items.length < this.totalItems;
    },
  },
  methods: {
    ...mapActions(['fetchItemsPaginated', 'initTableState']),

    /**
     * Load remote table data with current page size
     */
    async loadTableData() {
      // Build custom filters including the inverter/solar panel filter if enabled
      const customFilters = { ...this.tableStateStore.customFilters };

      if (this.filterInvertersOnly) {
        customFilters.solarPlants = [{ value: 'INVERTER' }];
      }

      if (this.filterSolarPanelsOnly) {
        customFilters.solarPlants = [{ value: 'SOLAR_PANELS' }];
      }

      await this.fetchItemsPaginated({
        pagination: {
          skip: 0,
          pageSize: this.currentPageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: {
          ...this.tableStateStore.filters,
          ...customFilters,
        },
      });
    },

    /**
     * Initialize/reset data loading (for new searches/filters)
     */
    async initializeData() {
      // Reset page size for new search/filter
      this.currentPageSize = this.initialPageSize;
      await this.loadTableData();
      console.log(`Initial load: ${this.items.length} items, ${this.totalItems} total available`);
    },

    /**
     * Load more items (for load more button)
     */
    async loadMoreItems() {
      if (this.loadingMore || !this.hasMoreItems) return;

      this.loadingMore = true;

      try {
        // Increase page size to load more items
        this.currentPageSize += this.loadMorePageSize;
        await this.loadTableData();
        console.log(
          `Load more: now showing ${this.items.length} of ${this.totalItems} items (pageSize: ${this.currentPageSize})`
        );
      } finally {
        this.loadingMore = false;
      }
    },

    /**
     * Handle search input with debouncing
     */
    onSearchInput() {
      // Clear existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Set new timeout for debounced search
      this.searchTimeout = setTimeout(() => {
        this.tableStateStore.filters.search.value = this.searchQuery || null;
        // Reset data for new search
        this.initializeData();
      }, 300);
    },

    addItem(item) {
      this.$emit('add', {
        ...item,
        addRemoveCount: item.quantity,
      });

      // Show success toast
      this.$toast.add({
        severity: 'success',
        summary: this.$t('SUCCESSFUL'),
        detail: `${item.quantity} ${item.description} ${this.$t('added')}`,
        life: 3000,
      });
    },

    removeItem(item) {
      this.$emit('remove', item);
    },

    isSelected(item) {
      return this.selectedItems.some((selected) => selected._id === item._id);
    },

    clearFilters() {
      this.selectedCategory = '';
      this.selectedSupplier = '';
      this.searchQuery = '';
      this.tableStateStore.filters.search.value = null;
      // Reload data with cleared filters
      this.initializeData();
    },
  },

  async created() {
    // Initialize table state for this component instance
    await this.initTableState(this.itemBrowserTableStateConfig);
  },

  async mounted() {
    // Load initial data
    await this.initializeData();
  },

  beforeDestroy() {
    // Clear any pending search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>

<style scoped lang="scss">
.item-browser {
  display: flex;
  flex-direction: column;
  height: 100%;

  .item-browser-header {
    flex-shrink: 0;
    .search-section {
      .input-group {
        .btn {
          border-left: 0;
        }
      }
    }
  }

  .item-browser-content {
    flex-grow: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    .items-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 1rem;

      .item-card {
        border: 1px solid #dee2e6;
        border-radius: 0.375rem;
        padding: 1rem;
        background: white;
        transition: all 0.2s ease;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        height: 100%;

        &:hover {
          border-color: $primary;
          box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
        }

        &.selected {
          border-color: $primary;
          background-color: #f8f9fa;
        }

        .item-image {
          text-align: center;
          margin-bottom: 0.75rem;

          .item-placeholder {
            font-size: 2rem;
            color: #6c757d;
          }
        }

        .item-info {
          flex-grow: 1;
          margin-bottom: 1rem;

          .item-title {
            font-size: 0.9rem;
            font-weight: 600;
            margin-bottom: 0.25rem;
            color: #495057;
          }

          .item-details {
            font-size: 0.8rem;
            color: #6c757d;
            margin-bottom: 0;

            .item-price {
              font-weight: 600;
            }
          }
        }

        .item-actions {
          margin-top: auto;

          .quantity-controls {
            display: flex;
            align-items: stretch;
            gap: 0.5rem;

            .quantity-input {
              flex: 1;
              min-width: 70px;
              height: 32px;
              padding: 0.375rem 0.5rem;
              border: 1px solid #ced4da;
              border-radius: 0.25rem;
              font-size: 0.875rem;
              line-height: 1.2;
            }

            .btn {
              height: 32px;
              min-width: 32px;
              padding: 0.375rem 0.5rem;
              display: flex;
              align-items: center;
              justify-content: center;
              line-height: 1;
            }
          }
        }
      }
    }

    .items-table {
      .quantity-input {
        width: 80px;
      }
    }

    .view-controls {
      .view-mode-toggle {
        .btn {
          border-radius: 0.25rem;

          &:first-child {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
          }

          &:last-child {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
          }
        }
      }
    }

    .empty-state {
      .fa {
        opacity: 0.5;
      }
    }
  }

  .load-more-section {
    flex-shrink: 0;
  }

  // Mobile responsiveness
  @media (max-width: 768px) {
    .item-browser {
      .items-grid {
        grid-template-columns: 1fr;
      }

      .view-controls {
        flex-direction: column;
        gap: 0.5rem;

        .results-info {
          order: -1;
        }
      }
    }
  }
}
</style>
